// apps/dashboard/src/lib/session.ts
/**
 * Client-side helpers for the backend-minted `aks_session` httpOnly cookie. These call this
 * app's own same-origin Next route handlers (src/app/api/auth/session/route.ts,
 * src/app/api/auth/me/route.ts), which proxy to {API}/v1/auth/session and {API}/v1/auth/me —
 * see docs/superpowers/specs/2026-07-18-session-cookie-auth-design.md §4.1/§4.3/§4.4.
 *
 * Canonical copy — apps/test-engine, apps/course-engine, apps/partner-portal keep matching
 * copies (same reason auth-cookie.ts used to be duplicated: no client-safe shared package
 * exists yet for this). resolveCookieUser is dashboard-only: the other apps read the
 * cookie's user straight from /api/auth/me in their AuthContext.
 */

import { toSeededUser, type SeededUser } from '@/lib/seeded-user';

/** Thrown by establishSession so callers can branch on the status instead of parsing a
 * message. A 401 specifically means this Firebase credential can never mint a session —
 * see establishSession's note on auth_time — and the only fix is a real re-authentication. */
export class SessionMintError extends Error {
  constructor(readonly status: number) {
    super(`Session mint failed: HTTP ${status}`);
    this.name = 'SessionMintError';
  }
}

/** Mints the aks_session cookie server-side. Call after EVERY successful sign-in.
 * Throws on a non-OK response — callers (healSession, the AuthContext bootstrap heal)
 * treat a resolved call as "cookie is now valid", so swallowing a failed mint here
 * turns every subsequent request into an unexplained 401.
 *
 * A 401 here is NOT retryable. The API mints with checkRevoked=true, so it rejects any
 * ID token whose auth_time predates a revokeRefreshTokens() call — and force-refreshing
 * the ID token does NOT advance auth_time, only a genuine re-authentication does. Callers
 * must send the user back through sign-in rather than re-minting in a loop. */
export async function establishSession(idToken: string): Promise<void> {
  const res = await fetch('/api/auth/session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken }),
    credentials: 'same-origin',
  });
  if (!res.ok) {
    throw new SessionMintError(res.status);
  }
}

/** Clears the aks_session cookie.
 *
 * `revoke` (default true) also calls revokeRefreshTokens() server-side, invalidating every
 * outstanding session cookie AND ID token for this account on EVERY device. That is correct
 * for a user-initiated logout and wrong for anything else — a client that merely finds its
 * own cookie unusable must pass revoke:false, or one device's transient failure signs the
 * user out everywhere and re-arms the same rejection for the credential it mints next. */
export async function endSession(options: { revoke?: boolean } = {}): Promise<void> {
  const { revoke = true } = options;
  const res = await fetch(`/api/auth/session${revoke ? '' : '?revoke=false'}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  // The route clears the cookie on every 2xx; anything else left it in place.
  if (!res.ok) throw new Error(`Failed to end session: ${res.status}`);
}

// AuthProvider holds its loading screen on resolveCookieUser when there is no server seed
// (every signed-out visit to /login included), so a wedged API must not hold the page. A
// timeout reads as "no usable cookie", like any other failure here.
const COOKIE_CHECK_TIMEOUT_MS = 3000;

/**
 * Resolves the user behind the `aks_session` cookie, or null when there is no usable cookie.
 *
 * Called when Firebase reports no local session. That alone does not mean signed out:
 * Firebase's storage is per origin, so a user who signed in on another Aksademia app, or
 * whose browser evicted IndexedDB but kept the cookie, has a good cookie and no Firebase user
 * here. The API serves that user, so the cookie decides, as it does in test-engine and
 * course-engine.
 *
 * GET /api/auth/me goes first because it is also the sweep: it clears `aks_session` when the
 * API rejects it. That sweep exists because the bootstrap heal only runs when Firebase HAS a
 * local user, and the case that most needs it is the opposite one: a dead cookie with NO
 * Firebase session beside it. middleware admits on presence alone, so that cookie keeps
 * passing the gate, the SSR seed keeps 401ing, and the user ping-pongs between the app and
 * /login with nothing ever clearing it. An iOS home-screen PWA hits this every time: its
 * storage is a separate jar from Safari's.
 *
 * Null covers absent, rejected and unreachable alike. An unreachable API therefore reads as
 * signed out for this page load; the cookie itself is left alone (the route only clears on a
 * 401), so the next load recovers.
 */
export async function resolveCookieUser(): Promise<SeededUser | null> {
  try {
    const me = await fetch('/api/auth/me', {
      credentials: 'same-origin',
      signal: AbortSignal.timeout(COOKIE_CHECK_TIMEOUT_MS),
    });
    if (!me.ok) return null;
    const { authenticated } = (await me.json()) as { authenticated?: boolean };
    if (!authenticated) return null;

    const profile = await fetch('/api/proxy/users/me', {
      credentials: 'same-origin',
      signal: AbortSignal.timeout(COOKIE_CHECK_TIMEOUT_MS),
    });
    if (!profile.ok) return null;
    return toSeededUser(await profile.json());
  } catch {
    return null;
  }
}

/**
 * True when /api/auth/me reports the caller unauthenticated (or the check itself fails in a
 * way that means "unknown" should NOT be treated as "healthy"). Used by the onAuthStateChanged
 * bootstrap to heal a Firebase session with no matching aks_session cookie yet (mid-rollout,
 * or the cookie expired independently of the ID token) — idempotent, safe to call often.
 */
export async function isSessionCookieMissing(): Promise<boolean> {
  try {
    const res = await fetch('/api/auth/me', { credentials: 'same-origin' });
    if (!res.ok) return true;
    const data = await res.json();
    return !data.authenticated;
  } catch {
    // Network error: don't spam establishSession on every transient failure — treat as
    // "unknown" and skip the heal this cycle; the next auth-state event retries it.
    return false;
  }
}
